import React from "react";
import {
  ArrowPathIcon,
  BugAntIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { APP_VERSION, getUser } from "../../Localstorage/localstorage";

const releaseGroups = [
  {
    version: "1.3.4",
    date: "Oktober 2026",
    title: "Berichten van de klant zijn meteen zichtbaar voor de therapeut.",
    highlights: [
      "Wanneer u een gesprek opent, worden alle berichten in die conversatie opnieuw geladen.",
      "Nieuwe berichten verschijnen sneller in de open chat, ook zonder zelf eerst te antwoorden.",
      "De gesprekslijst vernieuwt mee wanneer er een nieuw bericht binnenkomt.",
    ],
    fixes: [
      "Opgelost: therapeuten zagen soms geen binnenkomende berichten tot ze zelf iets verstuurden.",
      "Opgelost: meldingen verschenen wel, maar de chat toonde de tekst nog niet.",
      "Stabielere weergave van berichten bij het wisselen tussen gesprekken.",
    ],
    audiences: ["client", "admin", "mainAdmin"],
  },
  {
    version: "1.3.0",
    date: "September 2026",
    title: "Repeat Connect voelt rustiger, vollediger en professioneler.",
    highlights: [
      "Klanten krijgen een rustiger startscherm met hun volgende stap, open acties, berichten, documenten en voortgang op een plek.",
      "Berichten voelen sneller en duidelijker door een vernieuwde inbox, betere ongelezen meldingen en een overzichtelijkere chatervaring.",
      "Supportvragen zijn duidelijker zichtbaar, zodat klanten en het team sneller zien welke tickets nog aandacht vragen.",
      "Documenten, formulieren en metingen worden beter samengebracht, zodat klanten minder hoeven zoeken.",
      "De zijbalk kreeg een helderdere indeling met een vaste plek voor release nota's.",
    ],
    fixes: [
      "Minder herhaling op het klantdashboard.",
      "Betere bescherming rond berichten en bijlagen.",
      "Stabielere meldingen voor nieuwe berichten.",
      "Verbeterde laadervaring wanneer een onderdeel tijdelijk geen data heeft.",
    ],
    audiences: ["client", "admin", "mainAdmin"],
  },
  {
    version: "1.2",
    date: "April - augustus 2026",
    title: "Meer grip op afspraken, dagboeken en communicatie.",
    highlights: [
      "Afspraken en Calendly zijn betrouwbaarder gemaakt, zodat geplande momenten beter in Repeat Connect verschijnen.",
      "De mailbox werd uitgebreid voor duidelijker contact tussen klant en specialist.",
      "Voedingsdagboek, klachtenregistratie en vragenlijsten kregen extra verbeteringen voor invullen en opvolgen.",
      "Documenten en adviezen zijn makkelijker terug te vinden in de juiste categorie.",
    ],
    fixes: [
      "Oplossingen voor afspraakweergave en filters.",
      "Verbeteringen in login en sessies.",
      "Duidelijkere foutafhandeling wanneer gegevens niet geladen kunnen worden.",
    ],
    audiences: ["client", "admin", "mainAdmin"],
  },
  {
    version: "1.1.8",
    date: "April 2025",
    title: "Berichten en formulieren werden praktischer in dagelijks gebruik.",
    highlights: [
      "Berichten kregen verbeteringen zodat klant en specialist eenvoudiger contact houden.",
      "Formulieren en klachtenregistratie werden verder aangescherpt.",
      "De planning kreeg verbeteringen voor afspraken en opvolging.",
    ],
    fixes: [
      "Kleine storingen in berichten opgelost.",
      "Verbeteringen in afspraakdetails.",
      "Opschoning van overbodige technische meldingen.",
    ],
    audiences: ["client", "admin", "mainAdmin"],
  },
  {
    version: "1.1",
    date: "Januari - maart 2025",
    title: "De basis voor betere begeleiding werd versterkt.",
    highlights: [
      "Mailing, afspraken en klantinformatie werden verder uitgebouwd.",
      "Specialisten kregen meer overzicht in klantgegevens en opvolging.",
      "Wachtwoord en loginervaring werden verbeterd.",
    ],
    fixes: [
      "Betere stabiliteit bij aanmelden.",
      "Minder ruis door technische logs op te kuisen.",
      "Verbeteringen in het tonen van agenda- en klantgegevens.",
    ],
    audiences: ["admin", "mainAdmin"],
  },
  {
    version: "1.0",
    date: "November - december 2024",
    title: "Repeat Connect kreeg zijn eerste volledige klant- en teamflows.",
    highlights: [
      "Klanten konden starten met dagboek, vragenlijsten, documenten en voortgang.",
      "Specialisten kregen pagina's voor klanten, documenten, adviezen en evolutie.",
      "Klantenregistratie, klachtenformulieren en voedingsvragenlijsten werden toegevoegd.",
      "De eerste mailbox- en documentflows kwamen beschikbaar.",
    ],
    fixes: [
      "Verbeteringen aan layout, kleuren en navigatie.",
      "Stabielere formulieren voor klachten en voeding.",
      "Betere documentopbouw voor adviezen, plannen en uploads.",
    ],
    audiences: ["client", "admin", "mainAdmin"],
  },
];

const audienceMeta = {
  client: {
    title: "Voor klanten",
    description:
      "Wat u merkt wanneer u als klant inlogt: meer rust, minder zoeken en duidelijkere stappen.",
    icon: SparklesIcon,
  },
  admin: {
    title: "Voor therapeuten en admin",
    description:
      "Wat het team merkt bij opvolging, berichten, afspraken en klantbeheer.",
    icon: UserGroupIcon,
  },
  mainAdmin: {
    title: "Voor hoofdadmin",
    description:
      "Wat belangrijk is voor overzicht, beheer, veiligheid en organisatie.",
    icon: ShieldCheckIcon,
  },
};

const summaryCards = [
  {
    title: "Nieuwigheden",
    value: "Klant, team en beheer",
    icon: SparklesIcon,
  },
  {
    title: "Bugfixen",
    value: "Chatberichten, afspraken, formulieren",
    icon: BugAntIcon,
  },
  {
    title: "Laatste versie",
    value: `Versie ${APP_VERSION}`,
    icon: ArrowPathIcon,
  },
];

const clientFocus = [
  { label: "Dashboard", icon: ClipboardDocumentCheckIcon },
  { label: "Berichten", icon: ChatBubbleLeftRightIcon },
  { label: "Afspraken", icon: CalendarDaysIcon },
  { label: "Documenten", icon: DocumentTextIcon },
];

const getVisibleAudiences = (user) => {
  const isAdmin = Number(user?.role) === 1;
  const isMainAdmin = isAdmin && Number(user?.hoofd_admin) === 1;

  if (isMainAdmin) return ["client", "admin", "mainAdmin"];
  if (isAdmin) return ["admin"];
  return ["client"];
};

const ReleaseNotes = () => {
  const user = getUser();
  const visibleAudiences = getVisibleAudiences(user);
  const visibleGroups = releaseGroups.filter((release) =>
    release.audiences.some((audience) => visibleAudiences.includes(audience))
  );

  return (
    <main className="min-h-screen bg-wa-app px-4 py-6 text-wa-text sm:px-6 lg:px-8">
      <section className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-2xl border border-wa-border bg-wa-panel p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-wa-accent">
                Repeat Connect
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-normal text-wa-text sm:text-4xl">
                Release nota
              </h1>
              <p className="mt-3 text-base leading-7 text-wa-muted">
                Elke update in gewone mensentaal: wat er nieuw is, wat beter
                werkt en waarom dat voor u of uw team belangrijk is.
              </p>
            </div>
            <div className="rounded-2xl border border-wa-border bg-wa-panel2 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-wa-muted">
                Nieuwe versie
              </p>
              <p className="mt-1 text-2xl font-bold text-wa-text">
                Versie {APP_VERSION}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {summaryCards.map((card) => (
            <article
              key={card.title}
              className="rounded-2xl border border-wa-border bg-wa-panel p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-wa-accent/10 text-wa-accent">
                  <card.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-wa-text">
                    {card.title}
                  </p>
                  <p className="text-sm text-wa-muted">{card.value}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-6">
            {visibleAudiences.map((audience) => {
              const meta = audienceMeta[audience];
              const AudienceIcon = meta.icon;
              const releasesForAudience = visibleGroups.filter((release) =>
                release.audiences.includes(audience)
              );

              return (
                <section
                  key={audience}
                  className="rounded-2xl border border-wa-border bg-wa-panel shadow-sm"
                >
                  <div className="border-b border-wa-border p-5 sm:p-6">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-wa-accent/10 text-wa-accent">
                        <AudienceIcon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <div>
                        <h2 className="text-2xl font-bold text-wa-text">
                          {meta.title}
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-wa-muted">
                          {meta.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="divide-y divide-wa-border">
                    {releasesForAudience.map((release) => (
                      <article
                        key={`${audience}-${release.version}`}
                        className="p-5 sm:p-6"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-wa-accent">
                              Versie {release.version}
                            </p>
                            <h3 className="mt-1 text-xl font-bold text-wa-text">
                              {release.title}
                            </h3>
                          </div>
                          <p className="text-sm text-wa-muted">{release.date}</p>
                        </div>

                        <div className="mt-5 grid gap-5 lg:grid-cols-2">
                          <div>
                            <p className="text-sm font-semibold text-wa-text">
                              Nieuw voor u
                            </p>
                            <ul className="mt-3 space-y-3">
                              {release.highlights.map((item) => (
                                <li
                                  key={item}
                                  className="flex gap-3 text-sm leading-6 text-wa-muted"
                                >
                                  <CheckCircleIcon
                                    className="mt-0.5 h-5 w-5 shrink-0 text-wa-accent"
                                    aria-hidden="true"
                                  />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-wa-text">
                              Verbeterd of opgelost
                            </p>
                            <ul className="mt-3 space-y-3">
                              {release.fixes.map((item) => (
                                <li
                                  key={item}
                                  className="flex gap-3 text-sm leading-6 text-wa-muted"
                                >
                                  <BugAntIcon
                                    className="mt-0.5 h-5 w-5 shrink-0 text-wa-accent"
                                    aria-hidden="true"
                                  />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-wa-border bg-wa-panel p-5 shadow-sm">
              <h2 className="text-lg font-bold text-wa-text">In deze versie</h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {clientFocus.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl border border-wa-border bg-wa-panel2 p-3"
                  >
                    <item.icon className="h-5 w-5 text-wa-accent" aria-hidden="true" />
                    <p className="mt-2 text-sm font-semibold text-wa-text">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-wa-border bg-wa-panel p-5 shadow-sm">
              <h2 className="text-lg font-bold text-wa-text">
                Hoe deze nota is gemaakt
              </h2>
              <p className="mt-2 text-sm leading-6 text-wa-muted">
                De punten zijn afgeleid uit de gitgeschiedenis van Repeat
                Connect en herschreven naar taal voor klanten, therapeuten en
                beheerders.
              </p>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default ReleaseNotes;
